import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

const SUPABASE_URL = "https://aaydcwhckmrrokzsbpha.supabase.co";
const SUPABASE_ANON_KEY =
  "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImFheWRjd2hja21ycm9renNicGhhIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTA1Mjk0ODAsImV4cCI6MjA2NjEwNTQ4MH0.ow25Id-NdWl-kjXkz2I5278OcbpYa-As-q864N9UhjA";

const BOT_PATTERNS =
  /bot|crawl|spider|slurp|bingbot|googlebot|yandex|baidu|duckduck|semrush|ahrefs|mj12|dotbot|petalbot|bytespider|gptbot|chatgpt|facebookexternalhit|twitterbot|linkedinbot|whatsapp|telegrambot|discordbot|applebot/i;

function generateSessionId(): string {
  const stored = sessionStorage.getItem("heilys_session_id");
  if (stored) return stored;
  const id = crypto.randomUUID?.() ?? Math.random().toString(36).slice(2);
  sessionStorage.setItem("heilys_session_id", id);
  return id;
}

function isBot(): boolean {
  return BOT_PATTERNS.test(navigator.userAgent);
}

function isSelfExcluded(): boolean {
  try {
    return localStorage.getItem("heilys_no_track") === "1";
  } catch {
    return false;
  }
}

function trackPageView(path: string) {
  if (isBot() || isSelfExcluded()) return;

  const sessionId = generateSessionId();

  fetch(`${SUPABASE_URL}/rest/v1/page_views`, {
    method: "POST",
    headers: {
      apikey: SUPABASE_ANON_KEY,
      Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
      "Content-Type": "application/json",
      Prefer: "return=minimal",
    },
    body: JSON.stringify({
      session_id: sessionId,
      page_path: path,
      page_title: document.title,
      referrer: document.referrer || null,
      user_agent: navigator.userAgent,
      screen_width: window.innerWidth,
      hostname: "heilys.info",
    }),
  }).catch(() => {});
}

async function updateActiveSession(path: string) {
  if (isBot() || isSelfExcluded()) return;

  const sessionId = generateSessionId();

  try {
    await fetch(`${SUPABASE_URL}/rest/v1/active_sessions`, {
      method: "POST",
      headers: {
        apikey: SUPABASE_ANON_KEY,
        Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
        "Content-Type": "application/json",
        Prefer: "return=minimal,resolution=merge-duplicates",
      },
      body: JSON.stringify({
        session_id: sessionId,
        current_page: path,
        hostname: "heilys.info",
        started_at: new Date().toISOString(),
        last_seen_at: new Date().toISOString(),
      }),
    });
  } catch {
    // silently ignore – RLS may block anonymous inserts
  }
}

export function usePageTracking() {
  const location = useLocation();
  const lastPath = useRef<string>("");

  useEffect(() => {
    if (location.pathname === lastPath.current) return;
    lastPath.current = location.pathname;

    trackPageView(location.pathname);
    updateActiveSession(location.pathname);
  }, [location.pathname]);

  // Heartbeat for active sessions (every 30s)
  useEffect(() => {
    if (isBot() || isSelfExcluded()) return;

    const interval = setInterval(() => {
      updateActiveSession(location.pathname);
    }, 30_000);

    return () => clearInterval(interval);
  }, [location.pathname]);
}
